import type { ApolloCache, Reference } from '@apollo/client';
import gql from 'graphql-tag';
import { partition, uniq } from 'lodash';
import type { Apollo } from 'src/gql/generated';

const ARTIFACT_DOCUMENT_FRAGMENT = gql`
  fragment ArtifactDocumentFields on Document {
    id
    name
    deletedAt
  }
`;

/**
 * Inputs to {@link applyArtifactCreatedEventToCache}. Exported so unit
 * tests can construct them without needing the full GraphQL subscription
 * payload type chain.
 */
export interface ArtifactCreatedEventForCache {
  agentMessageId: string;
  artifactCreatedAt: string;
  documentDeletedAt: string | null;
  documentId: string;
  documentName: string;
  kindList: Apollo.AgentMessageJoinKind[];
  threadId: string;
}

type ThreadArtifactCacheRow = NonNullable<
  Apollo.GetAgentThreadQuery['agentThread']
>['artifacts'][number];
type ThreadDocumentArtifactCacheRow = Extract<
  ThreadArtifactCacheRow,
  { __typename: 'ThreadDocumentArtifact' }
>;

/**
 * Apply an `ArtifactCreatedEvent` to the Apollo cache: seed the
 * Document entity (so it has a normalized name + deletedAt) and
 * prepend a fresh ThreadDocumentArtifact-shaped row onto the parent
 * AgentThread.artifacts list. Pure with respect to its inputs — the
 * cache is the only side effect, which is why it lives in its own
 * file (no React, no transitive katex pull) and is testable in
 * isolation under vitest's plain Node environment.
 */
export function applyArtifactCreatedEventToCache(
  cache: ApolloCache,
  event: ArtifactCreatedEventForCache
): void {
  const row: ThreadDocumentArtifactCacheRow = {
    __typename: 'ThreadDocumentArtifact' as const,
    agentMessageId: event.agentMessageId,
    createdAt: event.artifactCreatedAt,
    document: {
      __typename: 'Document' as const,
      deletedAt: event.documentDeletedAt,
      id: event.documentId,
      name: event.documentName,
    },
    kindList: event.kindList,
  };
  /**
   * Seed the Document entity so Apollo doesn't show stale name /
   * deletedAt if the doc isn't already in the cache. Idempotent.
   */
  cache.writeFragment({
    data: row.document,
    fragment: ARTIFACT_DOCUMENT_FRAGMENT,
    id: cache.identify({ __typename: 'Document', id: row.document.id }),
  });
  cache.modify({
    fields: {
      artifacts(existing = [], { readField }) {
        if (!Array.isArray(existing)) return existing;

        const [matchingRows, remainingRows] = partition(existing, artifact => {
          const documentRef = readField<Reference>(
            'document' satisfies keyof ThreadDocumentArtifactCacheRow,
            artifact
          );
          return readField('id', documentRef) === event.documentId;
        });
        const existingKindList = matchingRows.flatMap(
          existingRow =>
            readField<readonly Apollo.AgentMessageJoinKind[]>('kindList', existingRow) ?? []
        );

        return [
          {
            ...row,
            kindList: uniq([...existingKindList, ...event.kindList]),
          },
          ...remainingRows,
        ];
      },
    },
    id: cache.identify({ __typename: 'AgentThread', id: event.threadId }),
  });
}
